// /src/pages/DashboardPage.js
import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, useTheme, Paper, Stack, IconButton, Skeleton } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import Grid from '@mui/material/Grid'; // Changed from Grid2 to Grid
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { useNavbar } from '../context/NavbarContext';
import Map from '../components/Map';
import RecentDataCard from '../components/RecentDataCard';

// Styled Paper for grid items
const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  ...theme.typography.body2,
  padding: theme.spacing(2),
  textAlign: 'left',
  color: theme.palette.text.secondary,
  borderRadius: '16px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'dark' ? '0 8px 28px rgba(0,0,0,0.45)' : '0 6px 18px rgba(16,24,40,0.10)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: theme.palette.mode === 'dark' ? '0 12px 36px rgba(0,0,0,0.50)' : '0 10px 24px rgba(16,24,40,0.14)',
  },
}));


const DashboardPage = () => {
  const { isNavbarOpen } = useNavbar();
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [loadingKpis, setLoadingKpis] = useState(true);
  const [loadingMap, setLoadingMap] = useState(true);

  const handleRefresh = () => {
    setLastUpdated(new Date());
  };

  useEffect(() => {
    const kpiTimer = setTimeout(() => setLoadingKpis(false), 600);
    const mapTimer = setTimeout(() => setLoadingMap(false), 800);
    return () => {
      clearTimeout(kpiTimer);
      clearTimeout(mapTimer);
    };
  }, []);
  const theme = useTheme();

  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh', 
      width: '100%', 
      background: theme.palette.background.default,
      margin: 0,
      padding: 0,
      overflow: 'auto',
    }}>
      <Container
        disableGutters
        sx={{
          minHeight: '100vh',
          width: '100%',
          flexGrow: 1,
          padding: {
            xs: '12px',
            sm: '16px',
            md: '20px',
            lg: '24px',
            xl: '32px'
          },
          marginLeft: {
            xs: isNavbarOpen ? '240px' : '70px',
            sm: isNavbarOpen ? '240px' : '70px',
            md: isNavbarOpen ? '240px' : '70px',
            lg: isNavbarOpen ? '240px' : '70px',
            xl: isNavbarOpen ? '240px' : '70px'
          },
          marginTop: {
            xs: '65px',
            sm: '64px',
            md: '64px',
            lg: '64px',
            xl: '64px'
          },
          transition: 'margin-left 0.3s, padding 0.3s',
          position: 'relative',
          maxWidth: '100% !important',
          overflow: 'auto',
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: {
            xs: '12px',
            sm: '16px',
            md: '16px',
            lg: '20px',
            xl: '24px'
          },
          margin: 0,
          padding: 0,
          width: '100%',
          minHeight: '100%',
          overflow: 'visible',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography 
                variant="h4" 
                gutterBottom 
                fontWeight="600"
                sx={{ 
                  color: theme.palette.text.primary,
                  letterSpacing: '-0.5px',
                  marginTop: 0,
                  fontSize: {
                    xs: '1.5rem',
                    sm: '1.75rem',
                    md: '2rem',
                    lg: '2.25rem',
                    xl: '2.5rem'
                  }
                }}
              >
                Dashboard
              </Typography>
              <Typography sx={{ 
                color: theme.palette.text.secondary, 
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                '&::before': {
                  content: '""',
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: theme.palette.accents?.emerald || theme.palette.success.main,
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.5 }
                  }
                }
              }}>Live overview • Last updated {lastUpdated.toLocaleTimeString()}</Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton 
                size="small" 
                onClick={handleRefresh} 
                aria-label="Refresh dashboard" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  transition: 'all 0.2s ease',
                  '&:hover': { 
                    color: theme.palette.brand?.royalBlue || theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.08),
                    transform: 'rotate(180deg)'
                  },
                  '&:active': {
                    transform: 'rotate(360deg)'
                  },
                  '&:focus-visible': { 
                    outline: `2px solid ${theme.palette.brand?.royalBlue || theme.palette.primary.main}`, 
                    outlineOffset: 2, 
                    borderRadius: '8px' 
                  }
                }}
              >
                <RefreshOutlinedIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>

          {/* KPIs are provided by RecentDataCard; removed duplicate KPI row */}

          <Box sx={{ mb: { xs: 1, sm: 2 } }}>
            {loadingKpis ? (
              <Grid container spacing={{ xs: 1, sm: 2 }}>
                {[0,1,2,3].map((i) => (
                  <Grid item xs={6} sm={6} md={3} key={i}>
                    <Skeleton variant="rounded" height={100} sx={{ borderRadius: '16px' }} />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <RecentDataCard />
            )}
          </Box>

          {/* First Row of Responsive Grid v2 */}
          <Grid container spacing={{
            xs: 1,
            sm: 2,
            md: 2,
            lg: 2,
            xl: 2
          }} sx={{ width: '100%' }}>
            {/* Map Card */}
            <Grid item xs={12}>
              <Item sx={{ 
                height: {
                  xs: 'calc(100vh - 280px)', // fit viewport on mobile below header/KPIs
                  sm: '400px',
                  md: '450px',
                  lg: '500px',
                  xl: '550px'
                },
                minHeight: { xs: '360px' },
                overflow: 'hidden',
                padding: 0,
                backgroundColor: theme.palette.background.paper
              }}>
                {loadingMap ? (
                  <Skeleton variant="rounded" sx={{ width: '100%', height: '100%', borderRadius: 0 }} />
                ) : (
                  <Box sx={{ 
                    position: 'relative',
                    height: '100%',
                    width: '100%',
                    '& .leaflet-container': {
                      height: '100%',
                      width: '100%',
                    }
                  }}>
                    <Map />
                  </Box>
                )}
              </Item>
            </Grid>
          </Grid>

          {/* Second Row of Responsive Grid v2 */}
          <Grid container spacing={{
            xs: 1,
            sm: 2,
            md: 2,
            lg: 2,
            xl: 2
          }} sx={{ width: '100%' }}>
            {/* Second Card */}
            {/* <Grid item xs={isMobile ? 12 : isTablet ? 6 : 6}>
              <Item>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#1A1F36',
                    fontWeight: '600',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: {
                      xs: '1rem',
                      sm: '1.1rem',
                      md: '1.25rem',
                      lg: '1.3rem',
                      xl: '1.4rem'
                    },
                    '&::before': {
                      content: '""',
                      width: '4px',
                      height: '20px',
                      backgroundColor: '#2196F3',
                      borderRadius: '2px',
                    }
                  }}
                >
                  Analytics Overview
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: '#4E5D78',
                    lineHeight: 1.6,
                    fontSize: {
                      xs: '0.875rem',
                      sm: '0.9rem',
                      md: '1rem',
                      lg: '1rem',
                      xl: '1.1rem'
                    }
                  }}
                >
                  Comprehensive analytics and insights for your operations.
                </Typography>
              </Item>
            </Grid> */}

            {/* Recent Logs Card */}
            {/* <Grid item xs={isMobile ? 12 : isTablet ? 6 : 6}>
              <Item>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#1A1F36',
                    fontWeight: '600',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: {
                      xs: '1rem',
                      sm: '1.1rem',
                      md: '1.25rem',
                      lg: '1.3rem',
                      xl: '1.4rem'
                    },
                    '&::before': {
                      content: '""',
                      width: '4px',
                      height: '20px',
                      backgroundColor: '#2196F3',
                      borderRadius: '2px',
                    }
                  }}
                >
                  Recent Logs
                </Typography>
                <RecentLogs />
              </Item>
            </Grid> */}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default DashboardPage;
